import { gql, useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import { notEmpty } from "@uplift-ltd/ts-helpers";
import React, { Fragment, useMemo } from "react";
import { PageIdType } from "__generated__/globalTypes";
import {
  STATIC_PAGE_QUERY,
  StaticPageQuery,
  StaticPageQueryVariables,
} from "components/StaticPageTemplate";
import Avatar from "components/common/Avatar";
import Container from "components/common/Container";
import Heading from "components/common/Heading";
import Link from "components/common/Link";
import { FullPageLoading } from "components/common/Loading";
import SectionHeader from "components/common/SectionHeader";
import { COURSE_AUTHOR_URL, COURSE_URL_BASE } from "constants/urls";
import { mapNodes } from "helpers/mapNodes";
import { SeoPageProps } from "helpers/seo";
import { usePageMeta } from "hooks/usePageMeta";
import { AUTHOR_INFO_FRAGMENT } from "../fragments";
import {
  CourseCatalogAuthorsQueryNode as CourseAuthor,
  CourseCatalogAuthorsQueryQuery as CourseCatalogAuthorsQuery,
} from "./__generated__/CourseCatalogAuthors";
import styles from "./CourseCatalog.module.css";

export const COURSE_CATALOG_AUTHORS_QUERY = gql`
  query CourseCatalogAuthorsQuery {
    # TODO: consider how we'll query for 500 courses?
    courseAuthors(first: 1000) {
      edges {
        node {
          id
          ...AuthorInfoFragment
          courseAuthorFields {
            firstAndMiddleNames
            lastName
          }
          featuredImage {
            node {
              id
              mediaItemUrl
              altText
            }
          }
          courses {
            id
            title
            ceUnits
            slug
            displayInCatalog
            categories {
              edges {
                node {
                  id
                }
              }
            }
          }
        }
      }
    }
  }

  ${AUTHOR_INFO_FRAGMENT}
`;

interface CatalogAuthorProps {
  author: CourseAuthor;
}

const CatalogAuthor = ({ author }: CatalogAuthorProps) => (
  <div className={styles.courseAuthorContainer}>
    {author.featuredImage?.node ? <Avatar size="lg" img={author.featuredImage.node} /> : null}
    <div className={styles.courseAuthorContent}>
      <div className={styles.courseAuthorName}>
        <Link href={makeUrl(COURSE_AUTHOR_URL, { slug: author.slug ?? "" })}>
          {author.courseAuthorFields?.lastName}, {author.courseAuthorFields?.firstAndMiddleNames}
          {author.courseAuthorFields?.title ? `, ${author.courseAuthorFields.title}` : null}
        </Link>
      </div>
      <div className={styles.courseAuthorList}>
        {author.courses?.filter(notEmpty).map(course => (
          <Link
            key={course.id}
            href={makeUrl(COURSE_URL_BASE, { slug: course.slug as string })}
            className={styles.courseAuthorItem}
          >
            {course.title}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const CourseCatalogAuthors: React.FC<SeoPageProps> = ({ title: initialTitle, seo: initialSeo }) => {
  const { data: pageMetaData, loading: pageMetaLoading } = useEnhancedQuery<
    StaticPageQuery,
    StaticPageQueryVariables
  >(STATIC_PAGE_QUERY, {
    variables: {
      idType: PageIdType.URI,
      id: "course-catalog-authors",
    },
  });
  const { data: courseCatalogData, loading: courseCatalogLoading } =
    useEnhancedQuery<CourseCatalogAuthorsQuery>(COURSE_CATALOG_AUTHORS_QUERY);

  const loading = pageMetaLoading || courseCatalogLoading;
  const { seo, title: pageTitle } = pageMetaData?.page || {};
  const { summary, title } = pageMetaData?.page?.heroSection || {};

  usePageMeta(initialTitle || title || pageTitle, initialSeo || seo);

  const authors = mapNodes(courseCatalogData?.courseAuthors).filter(
    author => author.courses.length > 0
  );

  const authorsByLastName = useMemo(() => {
    const lastNameLetter: { [key: string]: CourseAuthor[] } = {};
    // Iterate through courses
    authors.forEach(author => {
      // For every subject that they are related to store them in the subject dict
      const authorLastName = author.courseAuthorFields?.lastName || "A";
      const letter = authorLastName[0].toUpperCase();
      if (lastNameLetter[letter]) {
        lastNameLetter[letter].push(author);
      } else {
        lastNameLetter[letter] = [author];
      }
    });
    return lastNameLetter;
  }, [authors]);

  const authorLastNameLetters = Object.keys(authorsByLastName)
    .sort()
    .map(letter => {
      return {
        href: `#${letter}`,
        id: letter,
        label: letter,
      };
    });

  if (loading) return <FullPageLoading delayMs={100} />;

  return (
    <>
      <SectionHeader title={initialTitle || title || pageTitle || ""} summary={summary || ""} />
      <Container>
        <div className={styles.courseCatalogMainWrapper}>
          <div className={styles.courseCatalogSubWrapper}>
            <div className={styles.courseCatalogCustomToC}>
              {authorLastNameLetters.map(i => (
                <Link key={i.id} href={i.href} className={styles.courseCatalogCustomToCItem}>
                  {i.label}
                </Link>
              ))}
            </div>
            {authorLastNameLetters.map(letter => {
              return (
                <Fragment key={letter.id}>
                  <Heading id={letter.id} tag="h2" className={styles.courseHeading}>
                    {letter.label}
                  </Heading>
                  {authorsByLastName[letter.id]
                    .sort((a1, a2) => {
                      const a1Last = a1.courseAuthorFields?.lastName?.toLowerCase() || "";
                      const a2Last = a2.courseAuthorFields?.lastName?.toLowerCase() || "";
                      if (a1Last >= a2Last) {
                        return 1;
                      }
                      return -1;
                    })
                    .map(author => (
                      <CatalogAuthor key={author.id} author={author} />
                    ))}
                </Fragment>
              );
            })}
          </div>
        </div>
      </Container>
    </>
  );
};

export default CourseCatalogAuthors;
